import { Link, useNavigate } from '@tanstack/react-router';
import { CircleCheck, CircleX, FilePenLine, Landmark } from 'lucide-react';
import { useId } from 'react';
import type { EtaResultRow, SalesDocumentDetail } from '../../../api/types';
import { useAccess } from '../../../app/access';
import { useI18n } from '../../../i18n/I18nProvider';
import { UserText } from '../../../ui/Bidi';
import { Button } from '../../../ui/Button';
import { cx } from '../../../ui/cx';
import type { DocumentToolsActions } from '../DocumentTools';
import styles from '../Sales.module.css';
import { MockLabel } from '../status';

/** One ETA step (depth 0) with the field-level errors under it (depth 1+). */
interface StepGroup {
  head: EtaResultRow | null;
  fields: EtaResultRow[];
}

/** Rows in ETA's order → steps with their fields (a field before any step gets a bare group). */
export function groupResults(rows: readonly EtaResultRow[]): StepGroup[] {
  const groups: StepGroup[] = [];
  for (const row of rows) {
    const current = groups.at(-1);
    if (row.depth === 0) groups.push({ head: row, fields: [] });
    else if (current) current.fields.push(row);
    else groups.push({ head: null, fields: [row] });
  }
  return groups;
}

/**
 * The rows of the verdict: the stored rows (migration 0019), or — for a document refused before
 * they were stored — the raw `validationSteps` read as documented (`name`, `status`, `error`).
 */
export function verdictRows(detail: SalesDocumentDetail): EtaResultRow[] {
  const eta = detail.eta;
  if (!eta) return [];
  if (eta.results?.length) return eta.results;
  const steps = eta.state.validationResults?.validationSteps ?? [];
  return steps.map((step) => ({
    source: 'validation' as const,
    etaStatus: 'invalid' as const,
    environment: eta.state.environment ?? 'mock',
    depth: 0,
    stepName: step.name ?? null,
    stepStatus: step.status ?? null,
    errorCode: step.error?.errorCode ?? null,
    propertyPath: null,
    propertyName: null,
    messageEn: step.error?.error ?? null,
    messageAr: null,
    recordedAt: eta.state.updatedAt ?? '',
  }));
}

const REFUSED: ReadonlySet<string> = new Set(['invalid', 'not_accepted']);

/** ETA's text in the interface language first, the other language under it when ETA sent both. */
function VerdictMessage({ row }: { row: EtaResultRow }) {
  const { locale } = useI18n();
  const [first, firstLang, second, secondLang] =
    locale === 'ar'
      ? [row.messageAr, 'ar', row.messageEn, 'en']
      : [row.messageEn, 'en', row.messageAr, 'ar'];
  const main = first ?? second;
  if (!main) return null;
  const extra = first && second ? second : null;
  return (
    <>
      <p className={styles.verdictMessage} lang={first ? firstLang : secondLang}>
        <UserText>{main}</UserText>
      </p>
      {extra ? (
        <p className={styles.verdictMessageAlt} lang={secondLang}>
          <UserText>{extra}</UserText>
        </p>
      ) : null}
    </>
  );
}

/** Field-level reasons: the property path, ETA's code, and ETA's text. */
function VerdictFields({ fields, testId }: { fields: EtaResultRow[]; testId?: string }) {
  const { t } = useI18n();
  const v = t.detail.verdict;
  return (
    <ul className={styles.verdictFields} data-testid={testId}>
      {fields.map((f, i) => (
        <li
          key={i}
          className={styles.verdictField}
          data-testid="eta-field"
          data-path={f.propertyPath ?? ''}
          data-code={f.errorCode ?? ''}
        >
          <dl className={styles.verdictFieldMeta}>
            {f.propertyPath || f.propertyName ? (
              <div>
                <dt>{v.field}</dt>
                <dd className={styles.mono}>{f.propertyPath ?? f.propertyName}</dd>
              </div>
            ) : null}
            {f.errorCode ? (
              <div>
                <dt>{v.code}</dt>
                <dd className={styles.mono}>{f.errorCode}</dd>
              </div>
            ) : null}
          </dl>
          <VerdictMessage row={f} />
        </li>
      ))}
    </ul>
  );
}

/**
 * MF-INV-009 (WP-1b.2): the Tax Authority's verdict on a document it refused, field by field and
 * clearly labelled as the Authority's (our local checks before issue are a separate thing), with
 * the correction: «correct and resend» opens a NEW draft that supersedes this document. Shown for
 * an invalid or not-accepted document only; the toolbar above keeps the document's own tools.
 */
export function EtaVerdict({
  orgId,
  detail,
  actions,
}: {
  orgId: string;
  detail: SalesDocumentDetail;
  actions: DocumentToolsActions;
}) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const access = useAccess(orgId);
  const titleId = useId();
  const etaStatus = detail.eta?.state.status;
  if (!etaStatus || !REFUSED.has(etaStatus)) return null;
  const v = t.detail.verdict;
  const doc = detail.document;
  const rows = verdictRows(detail);
  const validation = groupResults(rows.filter((r) => r.source === 'validation'));
  const submission = rows.filter((r) => r.source === 'submission');
  const replacement = doc.supersededBy;
  const statusText = (status: string | null) =>
    status === 'Valid' ? v.passed : status === 'Invalid' ? v.failed : (status ?? '—');
  return (
    <section
      className={cx(styles.card, styles.verdict)}
      aria-labelledby={titleId}
      data-testid="eta-verdict"
      data-status={etaStatus}
    >
      <div className={styles.cardHead}>
        <h2 id={titleId} className={styles.cardTitle}>
          <span className={cx(styles.cardIcon, styles.verdictIcon)} aria-hidden="true">
            <Landmark size={16} />
          </span>
          {v.title}
        </h2>
        <MockLabel environment={detail.eta?.state.environment} />
      </div>
      <div className={styles.cardBody}>
        <p className={styles.verdictLead}>
          {etaStatus === 'not_accepted' ? `${v.leadSubmission} ` : ''}
          {v.lead}
        </p>

        {validation.length ? (
          <>
            <h3 className={styles.sectionLabel}>{v.steps}</h3>
            <ol className={styles.verdictSteps} data-testid="eta-steps">
              {validation.map((g, i) => {
                const head = g.head;
                const failed = head?.stepStatus !== 'Valid';
                return (
                  <li
                    key={i}
                    className={cx(
                      styles.verdictStep,
                      failed ? styles.stepFailed : styles.stepPassed,
                    )}
                    data-testid="eta-step"
                    data-step-status={head?.stepStatus ?? ''}
                  >
                    <div className={styles.verdictStepHead}>
                      {failed ? (
                        <CircleX size={16} aria-hidden="true" />
                      ) : (
                        <CircleCheck size={16} aria-hidden="true" />
                      )}
                      <span className={styles.verdictStepName}>
                        <UserText>{head?.stepName ?? '—'}</UserText>
                      </span>
                      <span className={styles.verdictStepStatus}>
                        {statusText(head?.stepStatus ?? null)}
                      </span>
                      {head?.errorCode ? (
                        <span className={cx(styles.mono, styles.verdictCode)}>
                          {head.errorCode}
                        </span>
                      ) : null}
                    </div>
                    {head ? <VerdictMessage row={head} /> : null}
                    {g.fields.length ? (
                      <VerdictFields fields={g.fields} />
                    ) : failed && !head?.messageEn && !head?.messageAr ? (
                      <p className={styles.note}>{v.noDetail}</p>
                    ) : null}
                  </li>
                );
              })}
            </ol>
          </>
        ) : null}

        {submission.length ? (
          <>
            <h3 className={styles.sectionLabel}>{v.submission}</h3>
            <VerdictFields fields={submission} testId="eta-submission-reasons" />
          </>
        ) : null}

        <div className={styles.verdictActions} data-testid="eta-correction">
          {replacement && replacement.status === 'draft' ? (
            <>
              <p className={styles.verdictLead}>
                {v.openDraft}{' '}
                <Link
                  to="/orgs/$orgId/sales/invoices/$docId"
                  params={{ orgId, docId: replacement.id }}
                  data-testid="eta-correction-draft"
                >
                  {replacement.number}
                </Link>
              </p>
              {access.can('invoice.edit') ? (
                <Button
                  variant="primary"
                  icon={<FilePenLine size={16} aria-hidden="true" />}
                  testId="eta-correct"
                  onPress={() =>
                    void navigate({
                      to: '/orgs/$orgId/sales/invoices/$docId/edit',
                      params: { orgId, docId: replacement.id },
                    })
                  }
                >
                  {v.continue(replacement.number ?? '')}
                </Button>
              ) : null}
            </>
          ) : replacement ? (
            <p className={styles.verdictLead}>
              {v.replaced}{' '}
              <Link
                to="/orgs/$orgId/sales/invoices/$docId"
                params={{ orgId, docId: replacement.id }}
                data-testid="eta-correction-replacement"
              >
                {replacement.number}
              </Link>
            </p>
          ) : doc.canSupersede ? (
            access.can('invoice.create') ? (
              <>
                <Button
                  variant="primary"
                  icon={<FilePenLine size={16} aria-hidden="true" />}
                  testId="eta-correct"
                  isPending={actions.isSuperseding}
                  onPress={() => actions.supersede(doc.id)}
                >
                  {v.correct}
                </Button>
                <p className={styles.note}>{v.correctHint}</p>
              </>
            ) : (
              <p className={styles.note}>{v.noPermission}</p>
            )
          ) : null}
        </div>
      </div>
    </section>
  );
}

/**
 * The reasons only (no passed steps, no actions): what the Tax Authority reported on the document
 * a correction draft replaces, shown in the editor next to — and apart from — our local checks.
 */
export function VerdictReasons({ detail }: { detail: SalesDocumentDetail }) {
  const rows = verdictRows(detail);
  const reasons: EtaResultRow[] = [];
  for (const g of groupResults(rows.filter((r) => r.source === 'validation'))) {
    if (g.head?.stepStatus === 'Valid') continue;
    if (g.fields.length) reasons.push(...g.fields);
    else if (g.head) reasons.push(g.head);
  }
  reasons.push(...rows.filter((r) => r.source === 'submission'));
  if (!reasons.length) return null;
  return <VerdictFields fields={reasons} testId="supersedes-reasons" />;
}
